import { useMemo } from 'react';
import { useI18n, useT } from '../lib/i18n';
import { useHost } from '../widget/WidgetRoot';
import { formatters, type Day } from '../lib/dates';
import { priceOf, type Accommodation, type AttributeDef, type Booking, type Quote } from '../lib/api';

/**
 * One accommodation, with what it costs and how it may be had.
 *
 * ---------------------------------------------------------------------------
 * THE THREE BOOKING OUTCOMES ARE THE POINT OF THIS CARD
 * ---------------------------------------------------------------------------
 *
 * `direct` is the ordinary case. The other two are what a naive results list
 * gets wrong:
 *
 *   contact     the park CAN do this; a person arranges it rather than the
 *               website. Hiding it loses a booking the park would have taken,
 *               so it is offered with the same prominence and a different verb.
 *   indicative  a price the park has not committed to. It is shown, because a
 *               guest deciding between two accommodations needs a number, and
 *               it is marked so it cannot be mistaken for a quote.
 *
 * ---------------------------------------------------------------------------
 * WHERE THE NUMBER COMES FROM
 * ---------------------------------------------------------------------------
 *
 * `/api/public/quote`, and nowhere else. This browser never computes a price of
 * its own; §7 is explicit that a client price is never believed, and the
 * corollary is that a client has no business presenting one as final. What it
 * may do is remember what the API said and show that instantly while asking
 * again — which the search does, sending the remembered figure as `expected` so
 * a disagreement is logged rather than resolved in silence.
 *
 * `priceOf` is used rather than `quote.total`, because the route returns
 * `total: 0` alongside `status: 'unavailable'` and reading the number without
 * the verdict advertises a free stay. §7 lists that as known and unfixed.
 */
export function ResultCard({
  accommodation, defs, quote, loading, arrival, nights, free, available, booking,
  chosen, onChoose, currency,
}: {
  accommodation: Accommodation;
  defs: readonly AttributeDef[];
  /** The park's, from configuration. Never assumed. */
  currency: string;
  quote: Quote | null;
  loading: boolean;
  arrival: Day;
  nights: number;
  /** Units free for these dates, from the availability scan. */
  free: number | null;
  /**
   * Whether this stay may be had, as `useSearch` decided it.
   *
   * PASSED IN rather than recomputed here. It used to be derived from the quote
   * a second time, which was a duplicated decision — and it broke the moment
   * `available` became optional for a deployment with no inventory, because a
   * card working it out for itself has no idea whether anybody is counting.
   * One place decides; every renderer is told.
   */
  available: boolean;
  booking: Booking;
  chosen: boolean;
  onChoose: () => void;
}) {
  const { showNightlyRate } = useHost();
  const t = useT();
  const { locale } = useI18n();
  const fmt = useMemo(() => formatters(locale, currency), [locale, currency]);

  const price = quote ? priceOf(quote) : null;
  const stay = t('search.nights', { count: nights });

  const classes = ['pw-card'];
  if (chosen) classes.push('pw-card-chosen');
  if (!available) classes.push('pw-card-off');

  return (
    <article className={classes.join(' ')} aria-labelledby={`pw-acc-${accommodation.id}`}>
      <div className="pw-card-head">
        {/* Operator data. Shown exactly as typed, never translated (§8). */}
        <h3 className="pw-h3" id={`pw-acc-${accommodation.id}`}>{accommodation.label}</h3>
        <BookingTag booking={booking} available={available} />
      </div>

      <Attributes accommodation={accommodation} defs={defs} />

      {quote?.restriction?.label && (
        <p className="pw-note">
          {t('results.restrictionWhy', { reason: quote.restriction.label })}
        </p>
      )}

      {booking === 'indicative' && available && (
        <p className="pw-note">{t('results.bookingIndicativeWhy')}</p>
      )}
      {quote?.priceChanged && (
        <p className="pw-banner pw-banner-warn">{t('results.priceChanged')}</p>
      )}

      <div className="pw-price-row">
        <div className="pw-stack-tight">
          {loading && (
            <span className="pw-busy">
              <span className="pw-spinner" aria-hidden="true" />
              {t('results.quoting')}
            </span>
          )}
          {!loading && price !== null && (
            <>
              <span
                className={booking === 'indicative' ? 'pw-price pw-price-indicative' : 'pw-price'}
                data-testid={`price-${accommodation.id}`}
              >
                {fmt.money(price)}
              </span>
              <span className="pw-price-sub">{t('results.total', { stay })}</span>
              {/* Off unless the host asks. See `WidgetOptions.showNightlyRate`:
                  an average per night is not a price anything can be bought
                  at, and the operator asked for it to go. */}
              {showNightlyRate && (
                <span className="pw-price-sub">
                  {t('results.perNight', { price: fmt.money(price / nights) })}
                </span>
              )}
              {quote && quote.extrasTotal > 0 && (
                <span className="pw-price-sub">
                  {t('results.extras', { price: fmt.money(quote.extrasTotal) })}
                </span>
              )}
            </>
          )}
          {/* Nothing here when the stay is not free: the tag at the top of the
              card has already said so, and saying it twice in one card reads as
              two different facts. */}
          {!loading && price === null && available && (
            <span className="pw-price-sub">{t('results.noPrice')}</span>
          )}
          {available && free !== null && free > 0 && free <= 3 && (
            <span className="pw-tag pw-tag-warn">{t('results.free', { count: free })}</span>
          )}
        </div>

        <button
          type="button"
          className="pw-btn pw-btn-primary"
          disabled={!available}
          onClick={onChoose}
        >
          {chosen ? t('results.chosen') : t('results.choose')}
        </button>
      </div>

      <span className="pw-sr">{`${fmt.full(arrival)} — ${fmt.full(arrival + nights)}`}</span>
    </article>
  );
}

function BookingTag({ booking, available }: {
  booking: 'direct' | 'contact' | 'indicative';
  available: boolean;
}) {
  const t = useT();
  if (!available) return <span className="pw-tag pw-tag-crit">{t('results.unavailable')}</span>;
  if (booking === 'contact') return <span className="pw-tag pw-tag-info">{t('results.bookingContact')}</span>;
  if (booking === 'indicative') return <span className="pw-tag pw-tag-warn">{t('results.bookingIndicative')}</span>;
  return <span className="pw-tag pw-tag-ok">{t('results.bookingDirect')}</span>;
}

/**
 * What the park says about this type.
 *
 * Rendered from the definitions rather than from the value map, so the order is
 * the operator's and a value whose definition was deleted stops appearing —
 * which is the same rule the API applies when it builds the map.
 */
function Attributes({ accommodation, defs }: {
  accommodation: Accommodation;
  defs: readonly AttributeDef[];
}) {
  const t = useT();
  const shown = defs
    .map((def) => ({ def, value: accommodation.attributes[def.id] }))
    .filter((x) => x.value !== undefined && x.value !== '');
  if (shown.length === 0) return null;
  return (
    <ul className="pw-attrs">
      {shown.map(({ def, value }) => (
        <li className="pw-attr" key={def.id}>
          {def.label}
          {': '}
          <b>
            {typeof value === 'boolean'
              ? (value ? t('results.attrYes') : t('results.attrNo'))
              : String(value)}
            {def.unit ? ` ${def.unit}` : ''}
          </b>
        </li>
      ))}
    </ul>
  );
}
